import { Cog6ToothIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { PropsWithChildren, useMemo, useRef, useState } from "react";

import {
  // Badge,
  Button,
  Divider,
  Headline,
  Paragraph,
  PlaceholderEditor,
  PlaceholderEditorVariant,
  PlaceholderListValue,
  PopoverPanel,
  Select,
  SelectItem,
  Small,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateExportTemplateFontChoices,
  ReportTemplateExportFontEnums,
  ReportTemplateType,
  useUpdateReportTemplateLogoMutation,
  useUpdateReportTemplateMutation,
} from "@api/graphql/types-and-hooks";
import {
  PlaceholderValues,
  convertDisplayPlaceholdersToKeys,
  convertKeyPlaceholdersToDisplay,
} from "@components/GoalsBank/constants";
import UploadLogo from "@components/ReportHeaderSection/UploadLogo";
import { convertReadableString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { TEMPLATE_EDITOR_TABS, TemplateEditorTab } from "@pages/Templates/TemplateEditor";
import { TreatmentPlanSettingsDialog } from "@pages/Templates/components/TemplateSettings/TreatmentPlanSettingsDialog";
import {
  useGetS3BucketReportTemplateLogoUrlRetriever,
  useGetS3BucketUploadReportTemplateLogoCallback,
} from "@pages/Templates/components/TemplateSettings/hooks";
import "@pages/Templates/components/TemplateSettings/templateSettings.css";

interface TemplateSettingsProps extends PropsWithChildren {
  readonly: boolean;
  reportTemplate: ReportTemplateType;
  reportTemplateRefetch: () => Promise<void> | unknown;
  reportSampleStatusRefetch?: () => Promise<void> | unknown;
  activeTab?: TemplateEditorTab;
}

const fonts: { name: string; fontFamily: string; className: string; value: string }[] = [
  {
    value: ApiReportTemplateExportTemplateFontChoices.Arial,
    name: "Arial",
    fontFamily: "Arial, sans-serif",
    className: "font-select-arial",
  },
  {
    value: ApiReportTemplateExportTemplateFontChoices.Calibri,
    name: "Calibri",
    fontFamily: "Calibri, sans-serif",
    className: "font-select-calibri",
  },
  {
    value: ApiReportTemplateExportTemplateFontChoices.Georgia,
    name: "Georgia",
    fontFamily: "Georgia, serif",
    className: "font-select-georgia",
  },
  {
    value: ApiReportTemplateExportTemplateFontChoices.PublicSans,
    name: "Public Sans",
    fontFamily: "Public Sans, sans-serif",
    className: "font-select-public-sans",
  },
  {
    value: ApiReportTemplateExportTemplateFontChoices.TimesNewRoman,
    name: "Times New Roman",
    fontFamily: "'Times New Roman', serif",
    className: "font-select-times-new-roman",
  },
];

const fontExampleText = "The quick brown fox jumps over the lazy dog";

export const TemplateSettings: React.FC<TemplateSettingsProps> = ({
  reportTemplate,
  readonly,
  reportTemplateRefetch,
  reportSampleStatusRefetch,
  activeTab,
}) => {
  const S3BucketReportTemplateLogoUrlRetriever = useGetS3BucketReportTemplateLogoUrlRetriever({
    reportTemplateId: reportTemplate?.id ?? "",
  });
  const S3BucketUploadReportTemplateLogoCallback = useGetS3BucketUploadReportTemplateLogoCallback({
    reportTemplateId: reportTemplate?.id ?? "",
    refetch: reportTemplateRefetch,
  });
  const updateLogoMutation = useUpdateReportTemplateLogoMutation();

  const ref = useRef<HTMLDivElement>(null);

  const [headerText, setHeaderText] = useState(convertKeyPlaceholdersToDisplay(reportTemplate.headerText ?? ""));
  const [footerText, setFooterText] = useState(convertKeyPlaceholdersToDisplay(reportTemplate.footerText ?? ""));

  const [exportTemplateFont, setExportTemplateFont] = useState<SelectItem | null>(
    reportTemplate.exportTemplateFont
      ? {
          id: reportTemplate.exportTemplateFont,
          primary: convertReadableString(reportTemplate.exportTemplateFont),
          secondary: fontExampleText,
        }
      : null,
  );

  const [treatmentPlanSettingsOpen, setTreatmentPlanSettingsOpen] = useState(false);

  // const [everyPageEnabled, setEveryPageEnabled] = useState(true);

  const updateReportTemplate = useUpdateReportTemplateMutation();

  const handleFontSelect = (val: SelectItem | null) => {
    setExportTemplateFont(val);
    reportTemplate.exportTemplateFont = val?.id as ApiReportTemplateExportTemplateFontChoices;
  };
  const handleSave = async () => {
    if (!reportTemplate) {
      notifyError("Could not save template: invalid template!");
      return;
    }
    const headerTextKeys = convertDisplayPlaceholdersToKeys(headerText);
    const footerTextKeys = convertDisplayPlaceholdersToKeys(footerText);

    const input = {
      reportTemplateId: reportTemplate.id || "",
      headerText: headerTextKeys,
      footerText: footerTextKeys,
      font: reportTemplate.exportTemplateFont as unknown as ReportTemplateExportFontEnums,
    };

    await updateReportTemplate.mutateAsync(
      { input },
      {
        onSuccess: (response) => {
          const data = response.updateReportTemplate;
          if (!data?.status) {
            notifyError(data?.message || "Could not save report template.");
            return;
          }
          notifySuccess(data.message || "Successfully saved report template");
          reportSampleStatusRefetch?.();
        },
        onError: (error) => {
          console.error(error);
          notifyError("Could not save report template.");
        },
      },
    );
  };

  const logoSave = async () => {
    await updateLogoMutation
      .mutateAsync({
        input: { reportTemplateId: reportTemplate?.id ?? "<missing-report-template-id>" },
      })
      .then((data) => {
        if (!data.updateReportTemplateLogo?.status) {
          notifyError(data.updateReportTemplateLogo?.message || "Could not update report template logo.");
          return;
        }
        reportTemplateRefetch();
        reportSampleStatusRefetch?.();
      })
      .catch((error) => {
        console.error("Error removing the header logo : ", error);
        notifyError("Error removing the header logo");
      });
  };

  const fontItems: SelectItem[] = useMemo(() => {
    if (!fonts || fonts.length === 0) return [];

    return fonts
      .map((font) => ({
        primary: font.name,
        id: font.value,
        secondary: fontExampleText,
        className: font.className,
      }))
      .sort((a, b) => (a.primary > b.primary ? 1 : -1));
  }, [fonts]);

  const popoverContent = (
    <div className="flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="shrink-0">
        <div className="w-100 flex justify-between p-2">
          <Headline size="medium">Export Settings</Headline>
          <XMarkIcon
            onClick={() => {
              ref?.current?.click();
            }}
            className="h-6 w-6 cursor-pointer bg-transparent text-text-primary"
          />
        </div>
        <Divider className="-mx-3"></Divider>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-2">
        <div className="p-2">
          <div className="flex w-full justify-between items-center">
            <Paragraph displayType="loud" colorType="primary">
              Organization Logo
            </Paragraph>
            {/* {everyPageEnabled && <Badge dot={false} appearance="neutral" text="Shows on every page" />} */}
          </div>
          <div>
            {readonly ? (
              <div className="flex pt-2 pb-2">
                <img
                  src={reportTemplate.templateLogoSignedUrl || reportTemplate.defaultLogoSignedUrl || ""}
                  alt="Invalid logo URL"
                  className="h-1/2 w-1/2"
                />
              </div>
            ) : (
              <div className="pb-8">
                <UploadLogo
                  logoSignedUrl={reportTemplate.templateLogoSignedUrl || ""}
                  onSave={logoSave}
                  displayOnlyMode={false}
                  S3BucketUrlRetriever={(params) => S3BucketReportTemplateLogoUrlRetriever({ ...params })}
                  S3BucketUploadCallback={(params) => S3BucketUploadReportTemplateLogoCallback({ ...params })}
                  reportFileUploadCompleteCallback={() => Promise.resolve()}
                  fileParseStatusRetriever={(fileId) =>
                    new Promise((resolve) => {
                      setTimeout(() => resolve({ fileId, status: "done" }), 1000);
                    })
                  }
                  removeFileCallback={() => Promise.resolve()}
                />
              </div>
            )}
          </div>
          {!readonly && <Small className="-mt-6">Recommended size: 200 x 80px</Small>}

          <div className="mt-4">
            <div className="flex w-full justify-between items-center">
              <Paragraph displayType="loud" colorType="primary">
                Header Text
              </Paragraph>
            </div>

            <PlaceholderEditor
              title=""
              className="my-3"
              existingContent={headerText || reportTemplate.headerText || ""}
              hintText="Type / for dynamic content"
              onChange={(value) => setHeaderText(value)}
              insertValues={PlaceholderValues as PlaceholderListValue[]}
              readonly={readonly}
              variant={PlaceholderEditorVariant.WYSIWYG}
            />
          </div>
          <div className="mt-4">
            <div className="flex w-full justify-between items-center">
              <Paragraph displayType="loud" colorType="primary">
                Footer Text
              </Paragraph>
            </div>
            <PlaceholderEditor
              title=""
              className="my-3"
              existingContent={footerText || reportTemplate.footerText || ""}
              hintText="Type / for dynamic content"
              onChange={(value) => setFooterText(value)}
              insertValues={PlaceholderValues as PlaceholderListValue[]}
              readonly={readonly}
              variant={PlaceholderEditorVariant.WYSIWYG}
            />
          </div>
          <div className="mt-4 pb-4">
            <Paragraph displayType="loud" colorType="primary">
              Font Family
            </Paragraph>
            <div className="[&_div.absolute]:p-0 [&_div]:w-full [&_div.absolute]:overflow-hidden my-3">
              <Select
                title="Select Font"
                items={fontItems}
                placeholderText="Choose a font"
                selected={exportTemplateFont}
                inputClassName={fonts.find((f) => f.name === exportTemplateFont?.primary)?.className || ""}
                setSelected={handleFontSelect}
                enableSearch
                hintText={
                  !readonly
                    ? "This font will be used for all text in your reports including headers, body text, and field labels."
                    : ""
                }
              />
            </div>
          </div>
        </div>

        {/* Fixed Footer with Buttons */}
        {!readonly && (
          <div className="shrink-0 border-t bg-white">
            <div className="flex space-x-2 p-4 justify-end">
              <Button
                appearance="secondary"
                className="px-6"
                text="Cancel"
                onClick={() => {
                  setHeaderText(convertKeyPlaceholdersToDisplay(reportTemplate.headerText ?? ""));
                  setFooterText(convertKeyPlaceholdersToDisplay(reportTemplate.footerText ?? ""));
                  ref?.current?.click();
                }}
              />
              <Button
                appearance="primary"
                text="Save Settings"
                className="px-6"
                onClick={async () => {
                  await handleSave();
                  ref?.current?.click();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const isProgrammingTab = activeTab === TEMPLATE_EDITOR_TABS.PROGRAMMING;

  return (
    <div className="flex items-center gap-2">
      {isProgrammingTab ? (
        <Button
          variant="secondary"
          buttonStyle="border"
          text={
            <div className="flex items-center">
              <Cog6ToothIcon className="mr-1 h-4 w-5 stroke-2" />
              <Small>Treatment Plan Settings</Small>
            </div>
          }
          onClick={() => setTreatmentPlanSettingsOpen(true)}
        />
      ) : (
        <PopoverPanel
          direction="bottom-right"
          width="w-[800px]"
          height="max-h-[800px] overflow-hidden"
          content={popoverContent}
        >
          <Button
            variant="secondary"
            buttonStyle="border"
            text={
              <div ref={ref} className="flex items-center">
                <Cog6ToothIcon className="mr-1 h-4 w-5 stroke-2" />
                <Small>Export Settings</Small>
              </div>
            }
          />
        </PopoverPanel>
      )}
      <TreatmentPlanSettingsDialog
        open={treatmentPlanSettingsOpen}
        setOpen={setTreatmentPlanSettingsOpen}
        reportTemplate={reportTemplate}
        reportTemplateRefetch={reportTemplateRefetch}
      />
    </div>
  );
};
